import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const EditorContainer = styled(Box)(({ theme }) => ({
  '& .ql-container': {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    minHeight: '400px',
    color: theme.palette.text.primary,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
  '& .ql-editor': {
    minHeight: '400px',
    color: theme.palette.text.primary,
    '&.ql-blank::before': {
      color: theme.palette.text.secondary,
      fontStyle: 'normal',
    },
    '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
      color: theme.palette.text.primary,
    },
    '& a': {
      color: theme.palette.primary.main,
    },
    '& code': {
      backgroundColor: 'rgba(99, 102, 241, 0.2)',
      color: theme.palette.primary.light,
      padding: '2px 6px',
      borderRadius: '4px',
    },
    '& pre.ql-syntax': {
      backgroundColor: '#1e1e1e',
      color: '#d4d4d4',
      padding: '16px',
      borderRadius: '8px',
      overflow: 'auto',
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2),
      margin: `${theme.spacing(2)} 0`,
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
      backgroundColor: 'rgba(99, 102, 241, 0.05)',
    },
  },
  '& .ql-toolbar': {
    backgroundColor: 'rgba(30, 41, 59, 0.8)',
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    '& .ql-stroke': {
      stroke: theme.palette.text.secondary,
    },
    '& .ql-fill': {
      fill: theme.palette.text.secondary,
    },
    '& .ql-picker-label': {
      color: theme.palette.text.secondary,
    },
    '& .ql-picker-options': {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    },
    '& button:hover, & button.ql-active': {
      '& .ql-stroke': {
        stroke: theme.palette.primary.main,
      },
      '& .ql-fill': {
        fill: theme.palette.primary.main,
      },
    },
  },
}));

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing your blog post...',
  error = false,
  helperText,
}) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ direction: 'rtl' }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link', 'image', 'video', 'code-block'],
        ['clean'],
      ],
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'script',
    'direction',
    'color',
    'background',
    'align',
    'link',
    'image',
    'video',
    'code-block',
  ];

  return (
    <Box>
      <EditorContainer>
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{
            backgroundColor: 'transparent',
          }}
        />
      </EditorContainer>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default RichTextEditor;

